import { invalidateAfterUserGroupMemberMutation, noddiAsync, URLKeys } from 'noddi-async';
import {
  ApiErrorMessageInternal,
  NoddiAsyncConfirmationDialog,
  NoddiInfoBanner,
  noddiToast,
  type SelectOption
} from 'noddi-ui-web';
import { useState } from 'react';
import type { MemberGroup } from './notificationGroupTypes';
import type { NotificationGroupOverviewTranslations } from './notificationTranslations';
import { ReplacementMemberSelect } from './ReplacementMemberSelect';

/** What removal reads, so a member leaving from their own page can pass their membership without the admin-only overview row. */
export type RemovableMember = Pick<
  MemberGroup,
  'userId' | 'userGroupMemberId' | 'firstName' | 'lastName' | 'isPrimaryContact'
>;

type Props = {
  userGroupId: number;
  member: RemovableMember;
  isOnlyAdmin: boolean;
  currentUserId?: number;
  translations: NotificationGroupOverviewTranslations;
  /** See `CarContactsOverview` — only the customer app needs its own success copy. */
  showMutationToasts: boolean;
  /**
   * The members list is admin-only, so an admin removing a driver picks who takes the cars.
   * A member leaving from their own settings keeps the backend's default handover.
   */
  chooseReplacement?: boolean;
  /**
   * Called instead of the refetch when the viewer removed themselves: they are no longer a
   * member, so every group-scoped query would come back 403. The host refreshes the session
   * and moves it to a group the user is still in.
   */
  onSelfRemoved?: () => void;
  /** Stays mounted after close so Radix can drop the body scroll lock. */
  open: boolean;
  onClose: () => void;
};

const memberName = (member: RemovableMember) => `${member.firstName} ${member.lastName}`.trim();

/**
 * Removing a member only ends their membership; their account is untouched. What
 * they leave behind (the cars they drive, open service reminders) has to land
 * with someone else in the organization.
 */
export const RemoveOrganizationMemberDialog = ({
  userGroupId,
  member,
  isOnlyAdmin,
  currentUserId,
  translations: t,
  showMutationToasts,
  chooseReplacement = false,
  onSelfRemoved,
  open,
  onClose
}: Props) => {
  const isSelf = member.userId === currentUserId;
  const [replacement, setReplacement] = useState<SelectOption<number>>();
  const [prevOpen, setPrevOpen] = useState(open);
  if (open !== prevOpen) {
    setPrevOpen(open);
    if (open) {
      setReplacement(undefined);
    }
  }
  // Primary contact is known locally. Other membership constraints remain authoritative on the backend.
  const blockedReason = member.isPrimaryContact
    ? t.primaryContactCannotBeRemoved
    : isOnlyAdmin
      ? isSelf
        ? t.makeAnotherAdminFirst
        : t.lastAdminHint
      : undefined;

  const { data: summary, error: summaryError } = noddiAsync.useGet({
    type: URLKeys.getUserGroupMemberRemovalSummary,
    input: { userGroupMemberId: member.userGroupMemberId },
    queryConfig: { enabled: open && !blockedReason }
  });
  const replacementId = summary?.defaultReplacementUserGroupMemberId ?? undefined;
  const needsDriverChoice = chooseReplacement && (summary?.managedCarsCount ?? 0) > 0;

  const {
    mutateAsync: deleteMember,
    isPending: isDeleting,
    error: deleteError
  } = noddiAsync.useDelete({
    type: URLKeys.deleteUserGroupMember,
    queryConfig: {
      meta: { suppressSuccessToast: showMutationToasts },
      onSuccess: () => {
        if (showMutationToasts) {
          noddiToast.success(t.memberRemoved(memberName(member)));
        }
        onClose();
        if (isSelf && onSelfRemoved) {
          onSelfRemoved();
          return;
        }
        return invalidateAfterUserGroupMemberMutation({ userGroupId });
      }
    }
  });

  const handsOverAnything = (summary?.managedCarsCount ?? 0) > 0 || (summary?.openBookingProposalsCount ?? 0) > 0;
  // The backend refuses to remove a driver nobody can take over from, so say so before the click.
  const hasNobodyToHandOverTo = !!summary && handsOverAnything && replacementId === undefined;

  return (
    <NoddiAsyncConfirmationDialog
      open={open}
      onOpenChange={(next) => !next && onClose()}
      title={isSelf ? t.leaveGroup : t.removeMember}
      description={isSelf ? t.leaveGroupDescription : t.removeMemberDescription(memberName(member))}
      cancelText={t.cancel}
      confirmText={isSelf ? t.leaveGroup : t.remove}
      confirmActionLoading={isDeleting}
      // Fail closed: without the summary the admin would confirm blind, with no
      // hand-over counts and no server-picked default replacement.
      confirmActionDisabled={
        blockedReason !== undefined || !summary || hasNobodyToHandOverTo || (needsDriverChoice && !replacement)
      }
      confirmAction={async () => {
        await deleteMember({
          userGroupMemberId: member.userGroupMemberId,
          replacementUserGroupMemberId: (needsDriverChoice ? replacement?.value : replacementId) ?? null
        });
      }}
    >
      {blockedReason || hasNobodyToHandOverTo ? (
        <NoddiInfoBanner variant='warning' texts={[{ value: blockedReason ?? t.noOtherMembers }]} />
      ) : (
        // Nothing to hand over means a plain confirmation. Each line is left out at zero.
        summary &&
        handsOverAnything && (
          <div className='flex flex-col gap-3'>
            {summary.managedCarsCount > 0 && (
              <p className='text-sm'>
                {isSelf
                  ? t.leaveHandoverCars(summary.managedCarsCount)
                  : t.handoverCars(memberName(member), summary.managedCarsCount)}
              </p>
            )}
            {needsDriverChoice && replacementId !== undefined && (
              <ReplacementMemberSelect
                userGroupId={userGroupId}
                excludeUserGroupMemberId={member.userGroupMemberId}
                defaultReplacementId={replacementId}
                selected={replacement}
                onSelect={setReplacement}
                translations={t}
              />
            )}
            {summary.openBookingProposalsCount > 0 && (
              <p className='text-sm'>
                {isSelf
                  ? t.leaveHandoverProposals(summary.openBookingProposalsCount)
                  : t.handoverProposals(memberName(member), summary.openBookingProposalsCount)}
              </p>
            )}
          </div>
        )
      )}
      {summaryError && <ApiErrorMessageInternal error={summaryError} />}
      {deleteError && <ApiErrorMessageInternal error={deleteError} />}
    </NoddiAsyncConfirmationDialog>
  );
};
